import type { ModalActions } from "@/hooks/useModal";
import { type Office } from "@/hooks/useOffices";
import type { ProjectSummary } from "@/hooks/useProjectBoardData";
import { useRecentlyChangedRowsApi } from "@/contexts/RecentlyChangedRowsContext";
import type { DealStage as DealStageRow } from "@/contexts/DealStagesContext";
import type { Category } from "@/contexts/CategoriesContext";
import { db, supabase } from "@/lib/supabase";
import { diffRecords, fullRowChanges, recordAudit } from "@/lib/audit";
import { assignmentAuditLabel, projectAuditLabel } from "@/utils/auditLabels";
import { withWeeklyHoursSummary } from "@/utils/auditWeeklyHours";
import type { Database } from "@/types/database.types";
import type { ProjectReindexResult } from "@/utils/projectUtils";
import { useEffect, useMemo, useRef, useState } from "react";
import { CreatableComboBox } from "../ComboBox";
import { ClientCombobox } from "../clients/ClientCombobox";
import { computeNumWeeks, computeProjectEndDate, currentWeekStartISO } from "@/utils/dateUtils";
import { getEffectiveProjectColor, isValidHexColor } from "@/utils/projectColor";
import { MonDateInput } from "../WeekStepper";
import { ProjectColorPicker } from "./ProjectColorPicker";
import {
    buildDuplicatedAssignmentDrafts,
    formatDuplicatedAssignmentEmployeeAuditNames,
    getDuplicatedProjectModalFormData,
    getProjectModalFormData,
    type ProjectModalFormData,
} from "@/utils/projectDuplication";

function parseLtC(raw: string): number | null {
    if (!raw.trim()) return null;
    if (!/^\d+$/.test(raw)) return null;
    const value = Number(raw);
    if (!Number.isFinite(value)) return null;
    if (!Number.isInteger(value)) return null;
    if (value < 0 || value > 100) return null;
    return value;
}

interface EditProjectModalProps extends ModalActions {
    project: ProjectSummary;
    offices: Office[];
    /**
     * Configured deal stages, passed in (not read from context) because the modal
     * renders in a separate React root via useModal and can't see app providers.
     */
    dealStages: DealStageRow[];
    /**
     * Configured categories, passed in (not read from context) for the same
     * separate-React-root reason as dealStages. Ordered by sort_order.
     */
    categories: Category[];
    onSuccess: () => void;
    formatRole: (id: string | null | undefined) => string;
    mode?: "edit" | "duplicate";
    onDuplicateProjectClick?: () => void;
    /** Called when user chooses "Delete Project"; typically opens hard-delete confirm modal. Edit modal should close before calling. */
    onDeleteProjectClick?: () => void;
}

export function EditProjectModal({
    project,
    offices = [],
    dealStages,
    categories,
    onSuccess,
    formatRole,
    mode = "edit",
    onDuplicateProjectClick,
    onDeleteProjectClick,
    close,
}: EditProjectModalProps) {
    const isDuplicateMode = mode === "duplicate";
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [techStackOptions, setTechStackOptions] = useState<string[]>([]);
    const [engagementTypeOptions, setEngagementTypeOptions] = useState<string[]>([]);
    const [keepAllAssignments, setKeepAllAssignments] = useState(false);
    const [createdDuplicateProjectId, setCreatedDuplicateProjectId] = useState<string | null>(null);
    const { markSelfEdit } = useRecentlyChangedRowsApi();

    // Reserved stages, identified by kind (not name).
    const lostStage = dealStages.find((s) => s.kind === "lost");
    const wonStage = dealStages.find((s) => s.kind === "won");

    const getInitialFormData = (targetMode: "edit" | "duplicate"): ProjectModalFormData =>
        targetMode === "duplicate"
            ? getDuplicatedProjectModalFormData(project)
            : getProjectModalFormData(project);

    const [formData, setFormData] = useState<ProjectModalFormData>(() => getInitialFormData(mode));
    const initialFormDataRef = useRef<ProjectModalFormData>(getInitialFormData(mode));
    const previousModeRef = useRef(mode);

    useEffect(() => {
        if (previousModeRef.current === mode) return;
        const nextFormData = getInitialFormData(mode);
        setFormData(nextFormData);
        initialFormDataRef.current = nextFormData;
        setKeepAllAssignments(false);
        setCreatedDuplicateProjectId(null);
        setError(null);
        previousModeRef.current = mode;
    }, [mode]);

    const isDirty = useMemo(() => {
        const initial = initialFormDataRef.current!;
        const arraysEqual = (a: string[], b: string[]) =>
            a.length === b.length && a.every((v, i) => v === b[i]);
        return (
            formData.name !== initial.name ||
            formData.color !== initial.color ||
            formData.office_id !== initial.office_id ||
            formData.client !== initial.client ||
            formData.client_id !== initial.client_id ||
            formData.start_date !== initial.start_date ||
            formData.num_weeks !== initial.num_weeks ||
            formData.budget !== initial.budget ||
            formData.description !== initial.description ||
            formData.potential_future_work !== initial.potential_future_work ||
            formData.success_ingredients !== initial.success_ingredients ||
            formData.deal_stage !== initial.deal_stage ||
            formData.likelihood_to_close !== initial.likelihood_to_close ||
            formData.category_id !== initial.category_id ||
            !arraysEqual(formData.tech_stack, initial.tech_stack) ||
            !arraysEqual(formData.engagement_type, initial.engagement_type)
        );
    }, [formData]);

    useEffect(() => {
        setError(null);
    }, []);

    const validateForm = (): boolean => {
        if (!formData.name.trim()) {
            setError("Project name is required");
            return false;
        }

        if (!formData.office_id) {
            setError("Office is required");
            return false;
        }

        const selectedOffice = offices.find((o) => o.id === formData.office_id);
        if (!selectedOffice || !selectedOffice.is_active) {
            setError("Cannot assign project to inactive office");
            return false;
        }

        if (!formData.start_date) {
            setError("Start date is required");
            return false;
        }

        if (formData.num_weeks < 1 || !Number.isInteger(formData.num_weeks)) {
            setError("Number of weeks must be a positive integer");
            return false;
        }

        if (!formData.deal_stage.trim()) {
            setError("Deal stage is required");
            return false;
        }

        const ltc = parseLtC(formData.likelihood_to_close);
        if (ltc === null) {
            setError("Likelyhood to Close must be an integer between 0 and 100");
            return false;
        }

        return true;
    };

    const handleDealStageChange = (rawDealStage: string) => {
        setFormData((prev) => {
            const stage = dealStages.find((s) => s.name === rawDealStage);
            const currentLtC = parseLtC(prev.likelihood_to_close);
            let nextLtC = prev.likelihood_to_close;
            if (stage) {
                if (stage.kind !== "open") {
                    // Reserved stages force their likelihood (Lost = 0, Won = 100).
                    nextLtC = String(stage.likelihood_to_close);
                } else if (currentLtC != null && stage.likelihood_to_close > currentLtC) {
                    // Advancing to a higher-likelihood stage bumps the project up to its baseline.
                    nextLtC = String(stage.likelihood_to_close);
                }
            }
            return { ...prev, deal_stage: rawDealStage, likelihood_to_close: nextLtC };
        });
    };

    const handleLikelyhoodToCloseChange = (rawLtC: string) => {
        setFormData((prev) => {
            const parsed = parseLtC(rawLtC);
            // Bidirectional reserved-stage rule: 0 -> Lost, 100 -> Won.
            let nextDealStage = prev.deal_stage;
            if (parsed === 0 && lostStage) {
                nextDealStage = lostStage.name;
            } else if (parsed === 100 && wonStage) {
                nextDealStage = wonStage.name;
            }
            return { ...prev, likelihood_to_close: rawLtC, deal_stage: nextDealStage };
        });
    };

    const createDuplicateProject = async (ltc: number, dealStageId: string) => {
        const projectData: Database["public"]["Tables"]["projects"]["Insert"] = {
            id: crypto.randomUUID(),
            name: formData.name.trim(),
            color: isValidHexColor(formData.color) ? formData.color : null,
            office_id: formData.office_id,
            client: formData.client.trim() || null,
            client_id: formData.client_id,
            start_date: formData.start_date,
            num_weeks: formData.num_weeks,
            budget: formData.budget ? parseFloat(formData.budget) : null,
            description: formData.description.trim() || null,
            potential_future_work: formData.potential_future_work.trim() || null,
            team_success_ingredients: formData.success_ingredients.trim() || null,
            tech_stack: formData.tech_stack,
            engagement_type: formData.engagement_type,
            deal_stage_id: dealStageId,
            likelihood_to_close: ltc,
            category_id: formData.category_id,
        };

        markSelfEdit("projects", projectData.id!);
        const { data: createdProject, error: createError } = await db.projects.create(projectData);
        if (createError) {
            setError(createError.message ?? "Failed to create project");
            return;
        }
        setCreatedDuplicateProjectId(createdProject.id);

        const newProjectLabel = projectAuditLabel(createdProject);
        recordAudit({
            table: "projects",
            recordId: createdProject.id,
            operation: "INSERT",
            changes: fullRowChanges(createdProject, "INSERT"),
            recordLabel: newProjectLabel,
            officeId: createdProject.office_id ?? null,
        });

        const assignmentDrafts = buildDuplicatedAssignmentDrafts(
            project.project_assignments,
            createdProject.id,
            formData.num_weeks,
            keepAllAssignments,
        );

        for (const draft of assignmentDrafts) {
            markSelfEdit("project_assignments", draft.insert.id);
            const { data: createdAssignment, error: assignmentError } =
                await db.projectAssignments.create(draft.insert);
            if (assignmentError) {
                onSuccess();
                setError(
                    `Project was created, but copying roles failed: ${assignmentError.message}. Close this modal and refresh the board before trying again.`,
                );
                return;
            }

            const assignmentForLabel = {
                ...createdAssignment,
                employees: draft.employees,
            };
            recordAudit({
                table: "project_assignments",
                recordId: createdAssignment.id,
                operation: "INSERT",
                changes: withWeeklyHoursSummary(
                    fullRowChanges(createdAssignment, "INSERT"),
                    createdProject.start_date,
                ),
                recordLabel: assignmentAuditLabel(newProjectLabel, assignmentForLabel, formatRole),
                officeId: createdProject.office_id ?? null,
            });

            if (draft.employees.length > 0) {
                const employeeAssignments = draft.employees.map((employee) => ({
                    assignment_id: createdAssignment.id,
                    employee_id: employee.id,
                }));

                markSelfEdit("project_assignments", createdAssignment.id);
                const { error: employeeInsertError } = await supabase
                    .from("assignment_employees")
                    .insert(employeeAssignments);
                if (employeeInsertError) {
                    onSuccess();
                    setError(
                        `Project and roles were created, but copying assignments failed: ${employeeInsertError.message}. Close this modal and refresh the board before trying again.`,
                    );
                    return;
                }

                recordAudit({
                    table: "project_assignments",
                    recordId: createdAssignment.id,
                    operation: "UPDATE",
                    changes: {
                        employees: {
                            old: [],
                            new: formatDuplicatedAssignmentEmployeeAuditNames(draft.employees),
                        },
                    },
                    recordLabel: assignmentAuditLabel(newProjectLabel, assignmentForLabel, formatRole),
                    officeId: createdProject.office_id ?? null,
                });
            }
        }

        onSuccess();
        close();
    };

    const updateExistingProject = async (ltc: number, dealStageId: string) => {
        const projectData: Database["public"]["Tables"]["projects"]["Update"] = {
            name: formData.name.trim(),
            color: isValidHexColor(formData.color) ? formData.color : null,
            office_id: formData.office_id,
            client: formData.client.trim() || null,
            client_id: formData.client_id,
            start_date: formData.start_date,
            num_weeks: formData.num_weeks,
            budget: formData.budget ? parseFloat(formData.budget) : null,
            description: formData.description.trim() || null,
            potential_future_work: formData.potential_future_work.trim() || null,
            team_success_ingredients: formData.success_ingredients.trim() || null,
            tech_stack: formData.tech_stack,
            engagement_type: formData.engagement_type,
            deal_stage_id: dealStageId,
            likelihood_to_close: ltc,
            category_id: formData.category_id,
        };

        // One atomic RPC: update the project AND reconcile every child assignment's
        // weekly_hours_allocated array to num_weeks in a single transaction. This
        // replaces the old update-then-per-assignment-loop, which was a series of
        // separate HTTP requests with no transaction (a failure partway left a
        // partial save). The RPC returns which assignments it resized so we can
        // still record the same audit entries and self-edit marks.
        markSelfEdit("projects", project.id);
        const { data: rpcData, error: updateError } = await db.projects.updateWithReindex(project.id, projectData);
        if (updateError || !rpcData) {
            setError(updateError?.message ?? "Failed to save project.");
            return;
        }
        const { resized } = rpcData as unknown as ProjectReindexResult;

        const oldProjectData = {
            name: project.name,
            color: project.color ?? null,
            office_id: project.office_id,
            client: project.client ?? null,
            client_id: project.client_id ?? null,
            start_date: project.start_date,
            num_weeks: project.num_weeks,
            budget: project.budget ?? null,
            description: project.description ?? null,
            potential_future_work: project.potential_future_work ?? null,
            team_success_ingredients: project.team_success_ingredients ?? null,
            tech_stack: project.tech_stack ?? [],
            engagement_type: project.engagement_type ?? [],
            deal_stage_id: project.deal_stage?.id ?? null,
            likelihood_to_close: project.likelihood_to_close,
            category_id: project.category_id,
        };
        // Use the new name but the original project's archived status, so renaming
        // an archived project still flags it as (ARCHIVED) in the audit log.
        const auditLabel = projectAuditLabel({ ...project, name: projectData.name ?? project.name });
        recordAudit({
            table: "projects",
            recordId: project.id,
            operation: "UPDATE",
            changes: diffRecords(oldProjectData, projectData),
            recordLabel: auditLabel,
            officeId: projectData.office_id ?? project.office_id ?? null,
        });

        // Audit + self-edit the assignments the RPC actually resized.
        for (const change of resized) {
            const assignment = project.project_assignments.find((a) => a.id === change.id);
            markSelfEdit("project_assignments", change.id);
            recordAudit({
                table: "project_assignments",
                recordId: change.id,
                operation: "UPDATE",
                changes: withWeeklyHoursSummary(
                    { weekly_hours_allocated: { old: change.old, new: change.new } },
                    project.start_date,
                ),
                recordLabel: assignment ? assignmentAuditLabel(auditLabel, assignment, formatRole) : auditLabel,
                officeId: projectData.office_id ?? project.office_id ?? null,
            });
        }

        onSuccess();
        close();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        try {
            const ltc = parseLtC(formData.likelihood_to_close);
            if (ltc === null) {
                setError("Likelyhood to Close must be an integer between 0 and 100");
                return;
            }

            const dealStageId = dealStages.find((s) => s.name === formData.deal_stage)?.id;
            if (!dealStageId) {
                setError("Invalid deal stage");
                return;
            }

            if (isDuplicateMode) {
                await createDuplicateProject(ltc, dealStageId);
            } else {
                await updateExistingProject(ltc, dealStageId);
            }
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : isDuplicateMode ? "Failed to create project" : "Failed to update project",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="modal-form">
            {error && (
                <div className="error-banner">
                    <span className="error-banner__message">{error}</span>
                </div>
            )}

            <div className="form-field">
                <label htmlFor="project-name" className="form-label">
                    Project Name <span className="required">*</span>
                </label>
                <input
                    id="project-name"
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    required
                    disabled={submitting}
                    autoFocus
                />
            </div>

            <div className="form-field">
                <label className="form-label" htmlFor="project-color">
                    Color
                </label>
                <div className="project-color-field">
                    <ProjectColorPicker
                        id="project-color"
                        projectId={project.id}
                        color={formData.color}
                        onPreview={(hex) => setFormData({ ...formData, color: hex })}
                        onCommit={(hex) => setFormData({ ...formData, color: hex })}
                        className="project-color-swatch--modal"
                        title="Project color"
                        ariaLabel="Project color"
                    />
                    <span className="project-color-field__value">
                        {getEffectiveProjectColor(project.id, formData.color)}
                        {formData.color == null ? " (auto)" : ""}
                    </span>
                </div>
            </div>

            <div className="form-row">
                <div className="form-field">
                    <label htmlFor="project-office" className="form-label">
                        Office <span className="required">*</span>
                    </label>
                    <select
                        id="project-office"
                        className="form-select"
                        value={formData.office_id}
                        onChange={e => setFormData({ ...formData, office_id: e.target.value })}
                        required
                        disabled={submitting}
                    >
                        <option value="">Select an office</option>
                        {offices.map(office => (
                            <option key={office.id} value={office.id}>
                                {office.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-field">
                    <label htmlFor="project-client" className="form-label">
                        Client
                    </label>
                    <ClientCombobox
                        id="project-client"
                        value={{ id: formData.client_id, name: formData.client }}
                        onChange={sel =>
                            setFormData({ ...formData, client_id: sel.id, client: sel.name })
                        }
                        disabled={submitting}
                        placeholder="Select or type a client..."
                    />
                </div>
            </div>

            <div className="form-row">
                <div className="form-field">
                    <label htmlFor="project-budget" className="form-label">
                        Budget
                    </label>
                    <input
                        id="project-budget"
                        type="number"
                        className="form-input"
                        value={formData.budget}
                        onChange={e => setFormData({ ...formData, budget: e.target.value })}
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        disabled={submitting}
                    />
                </div>
            </div>

            <div className="form-row">
                <div className="form-field">
                    <label htmlFor="project-deal-stage" className="form-label">
                        Deal Stage<span className="required">*</span>
                    </label>
                    <select
                        id="project-deal-stage"
                        className="form-select"
                        value={formData.deal_stage}
                        onChange={e => handleDealStageChange(e.target.value)}
                        required
                        disabled={submitting}
                    >
                        {dealStages.map((stage) => (
                            <option key={stage.id} value={stage.name}>
                                {stage.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-field">
                    <label htmlFor="project-ltc" className="form-label">
                        Likelyhood to Close<span className="required">*</span>
                    </label>
                    <input
                        id="project-ltc"
                        type="number"
                        className="form-input"
                        value={formData.likelihood_to_close}
                        onChange={e => handleLikelyhoodToCloseChange(e.target.value)}
                        min="0"
                        max="100"
                        step="1"
                        placeholder="0-100"
                        required
                        disabled={submitting}
                    />
                </div>
            </div>

            <div className="form-row">
                <div className="form-field">
                    <label htmlFor="project-category" className="form-label">
                        Category
                    </label>
                    <select
                        id="project-category"
                        className="form-select"
                        value={formData.category_id}
                        onChange={e => setFormData({ ...formData, category_id: e.target.value })}
                        disabled={submitting}
                    >
                        {categories.map((category) => (
                            <option key={category.id} value={category.id}>
                                {category.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="form-row">
                <div className="form-field">
                    <label htmlFor="project-start-date" className="form-label">
                        Start Date <span className="required">*</span>
                    </label>
                    <MonDateInput
                        id="project-start-date"
                        className="form-input"
                        value={formData.start_date}
                        minDate={currentWeekStartISO()}
                        onChange={(value) => setFormData({ ...formData, start_date: value })}
                        disabled={submitting}
                    />
                </div>

                <div className="form-field">
                    <label htmlFor="project-end-date" className="form-label">
                        End Date <span className="required">*</span>
                    </label>
                    <MonDateInput
                        id="project-end-date"
                        className="form-input"
                        value={computeProjectEndDate(formData.start_date, formData.num_weeks)}
                        minDate={computeProjectEndDate(formData.start_date, 2)}
                        onChange={(value) => setFormData({ ...formData, num_weeks: computeNumWeeks(formData.start_date, value) })}
                        disabled={submitting}
                    />
                </div>
            </div>

            <div className="form-row">
                <div className="form-field">
                    <label htmlFor="team-success-ingredients" className="form-label">
                        Team Success Ingredients
                    </label>
                    <input
                        id="team-success-ingredients"
                        type="text"
                        className="form-input"
                        value={formData.success_ingredients}
                        onChange={e => setFormData({ ...formData, success_ingredients: e.target.value })}
                        placeholder="Team Success Ingredients"
                        disabled={submitting}
                    />
                </div>
                <div className="form-field">
                    <label htmlFor="potential-future-work" className="form-label">
                        Potential Future Work
                    </label>
                    <input
                        id="potential-future-work"
                        type="text"
                        className="form-input"
                        value={formData.potential_future_work}
                        onChange={e => setFormData({ ...formData, potential_future_work: e.target.value })}
                        placeholder="Potential Future Work"
                        disabled={submitting}
                    />
                </div>
            </div>

            <div className="form-field">
                <label htmlFor="project-description" className="form-label">
                    Description
                </label>
                <textarea
                    id="project-description"
                    className="form-textarea"
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    disabled={submitting}
                />
            </div>
            <div className="form-row">
                <div className="form-field">
                    <label htmlFor="project-tech-stack" className="form-label">
                        Tech Stack
                    </label>
                    <CreatableComboBox
                        value={formData.tech_stack}
                        onChange={(value: string | string[]) => setFormData({ ...formData, tech_stack: value as string[] })}
                        options={techStackOptions}
                        multiple={true}
                        placeholder="Select or add tech stack..."
                        onCreateOption={(newVal: string) => setTechStackOptions([...techStackOptions, newVal])}
                    />
                </div>
                <div className="form-field">
                    <label htmlFor="project-engagement-type" className="form-label">
                        Engagement Type
                    </label>
                    <CreatableComboBox
                        value={formData.engagement_type}
                        onChange={(value: string | string[]) => setFormData({ ...formData, engagement_type: value as string[] })}
                        options={engagementTypeOptions}
                        multiple={true}
                        placeholder="Select engagement type..."
                        onCreateOption={(newVal: string) => setEngagementTypeOptions([...engagementTypeOptions, newVal])}
                    />
                </div>
            </div>
            {isDuplicateMode && (
                <label className="checkbox duplicate-project__assignments-checkbox">
                    <input
                        type="checkbox"
                        checked={keepAllAssignments}
                        onChange={(e) => setKeepAllAssignments(e.target.checked)}
                        disabled={submitting || project.project_assignments.length === 0}
                    />
                    <span>Keep all assignments</span>
                </label>
            )}
            <div className="modal-form-divider"></div>
            <div className="modal-actions modal-actions--space-between">
                {isDuplicateMode ? (
                    <span />
                ) : (
                    <div className="modal-actions">
                        <button
                            type="button"
                            className="danger-button"
                            onClick={() => {
                                close();
                                onDeleteProjectClick?.();
                            }}
                            disabled={submitting}
                        >
                            Delete Project
                        </button>
                        <button
                            type="button"
                            className="secondary-button"
                            onClick={onDuplicateProjectClick}
                            disabled={submitting}
                        >
                            Duplicate Project
                        </button>
                    </div>
                )}
                <div className="modal-actions">
                    {(isDuplicateMode || isDirty) && (
                        <button
                            type="button"
                            className="secondary-button"
                            onClick={close}
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                    )}
                    <button
                        type="submit"
                        className="primary-button"
                        disabled={
                            submitting ||
                            (!isDuplicateMode && !isDirty) ||
                            (isDuplicateMode && createdDuplicateProjectId !== null)
                        }
                    >
                        {createdDuplicateProjectId
                            ? "Project Created"
                            : submitting
                                ? isDuplicateMode ? "Creating..." : "Saving..."
                                : isDuplicateMode ? "Create Project" : "Save Changes"}
                    </button>
                </div>
            </div>
        </form>
    );
}
